import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Zoom,
  Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import AlarmIcon from '@mui/icons-material/Alarm';
import { useNavigate } from 'react-router-dom';
import { useTimer } from 'react-timer-hook';
import { useOktaAuth } from '@okta/okta-react';

import useSessionContext from 'src/hooks/useSessionContext';
import useHandleError from 'src/hooks/useHandleError';

const useStyles = makeStyles(() => ({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  dialogTitle: {
    textAlign: 'center'
  }
}));

const digit = (value) => {
  const leftDigit = value >= 10 ? value.toString()[0] : '0';
  const rightDigit = value >= 10 ? value.toString()[1] : value.toString();
  return `${leftDigit}${rightDigit}`;
};

const Transition = React.forwardRef((props, ref) => (
  <Zoom in ref={ref} {...props} />
));

const getExpiryTimestamp = () => {
  const expiryTimestamp = new Date();
  expiryTimestamp.setSeconds(expiryTimestamp.getSeconds() + 30); // 30 seconds timer

  return expiryTimestamp;
};

const SessionTimeoutDialog = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { oktaAuth } = useOktaAuth();
  const { isIdle, keepAlive, clearTimer } = useSessionContext();
  const { seconds, minutes, pause, restart } = useTimer({
    autoStart: false,
    expiryTimestamp: getExpiryTimestamp(),
    onExpire: () => {
      clearTimer();
      setTimeout(() => {
        navigate('/logout');
      }, 100);
    }
  });
  const { handleError } = useHandleError();

  const handleReset = () => {
    try {
      pause();
      setOpen(false);
      keepAlive();
    } catch (error) {
      handleError(error);
      setTimeout(window.location.reload, 1000);
    }
  };

  useEffect(() => {
    (async () => {
      if (isIdle && (await oktaAuth.isAuthenticated())) {
        setOpen(true);
        restart(getExpiryTimestamp(), true);
      } else {
        setOpen(false);
        pause();
      }
    })();
  }, [isIdle]);

  return (
    <Dialog
      fullWidth={false}
      maxWidth="xl"
      open={open}
      TransitionComponent={Transition}
    >
      <DialogTitle variant="h5" className={classes.dialogTitle}>
        Session Timeout
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <AlarmIcon />
        <Typography variant="subtitle1" gutterBottom={false}>
          Your session is about to expire.
        </Typography>
        <Typography variant="subtitle2">
          {digit(minutes)}:{digit(seconds)}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center' }}>
        <Button
          onClick={handleReset}
          color="primary"
          autoFocus
          variant="contained"
        >
          Reset
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeoutDialog;
